import React, { useMemo } from 'react';
import ScheduleSheet from './ScheduleSheet';

const S = {
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 },
  btn: (bg = '#7b4f2f') => ({ padding: '8px 14px', border: 'none', borderRadius: 8, background: bg, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }),
  btnGhost: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
};

function monthlyStatsOf(schedule) {
  const raw = schedule?.monthly_stats || schedule?.monthlyStats || {};
  const { personStats, ...rest } = raw;
  return rest && typeof rest === 'object' ? rest : {};
}

export default function ScheduleBoard({
  month,
  schedulePack,
  overview,
  busy,
  boardMode,
  setBoardMode,
  saveStatus,
  submittedSet,
  gapList,
  importOpen,
  setImportOpen,
  importText,
  setImportText,
  sheetNotes,
  onImportJson,
  onDownloadXlsx,
  onJumpDay,
  onPatch,
}) {
  const statsMap = useMemo(() => monthlyStatsOf(schedulePack?.schedule), [schedulePack]);
  const statsRows = useMemo(() => {
    const names = Object.keys(statsMap);
    if (names.length) return names.map((person) => ({ person, st: statsMap[person] }));
    return (schedulePack?.people || []).map((p) => ({ person: p.person, st: statsMap[p.person] }));
  }, [schedulePack, statsMap]);

  return (
    <div className="schedule-tab-pane">
      <div className="schedule-tab-toolbar">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" style={S.btnGhost} onClick={() => setImportOpen((v) => !v)}>JSON 匯入</button>
          <button
            type="button"
            style={S.btnGhost}
            disabled={!schedulePack?.schedule}
            onClick={onDownloadXlsx}
          >
            Excel
          </button>
          <button
            type="button"
            style={{ ...S.btnGhost, background: boardMode === 'view' ? '#7b4f2f' : '#fff', color: boardMode === 'view' ? '#fff' : '#374151' }}
            onClick={() => setBoardMode('view')}
          >
            檢視
          </button>
          <button
            type="button"
            style={{ ...S.btnGhost, background: boardMode === 'edit' ? '#7b4f2f' : '#fff', color: boardMode === 'edit' ? '#fff' : '#374151' }}
            onClick={() => setBoardMode('edit')}
          >
            調整
          </button>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            狀態：{schedulePack?.schedule?.status || '尚無'}
          </span>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            已送 {overview?.submittedCount ?? '—'}/{overview?.requiredCount ?? '—'}
          </span>
        </div>
        {importOpen && (
          <div style={{ marginTop: 8 }}>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={8}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
              placeholder='{"month":"2026-08","assignments":[{"day":1,"person":"姓名","start":8,"end":17,"label":"8-17","counts_toward_counter":true}]}'
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <label style={{ ...S.btnGhost, cursor: 'pointer', margin: 0 }}>
                選 JSON 檔
                <input
                  type="file"
                  accept="application/json,.json"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    e.target.value = '';
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => setImportText(String(reader.result || ''));
                    reader.readAsText(file);
                  }}
                />
              </label>
              <button type="button" style={S.btn()} disabled={busy} onClick={onImportJson}>匯入覆蓋草案</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 240px', gap: 12, alignItems: 'start' }}>
        <div>
          {schedulePack?.schedule ? (
            <ScheduleSheet
              month={month}
              dayHeaders={schedulePack.dayHeaders || []}
              people={schedulePack.people || []}
              assignments={schedulePack.schedule?.assignments || []}
              activitiesByDay={schedulePack.activitiesByDay || {}}
              shopNotesByDay={schedulePack.shopNotesByDay || sheetNotes.shopNotesByDay || {}}
              volunteersByDay={schedulePack.volunteersByDay || sheetNotes.volunteersByDay || {}}
              dayNotes={schedulePack.dayNotes || sheetNotes.dayNotes || {}}
              availabilityByPerson={schedulePack.availabilityByPerson || {}}
              canEdit={boardMode === 'edit'}
              canEditAvailability={boardMode === 'edit'}
              showAvailability={boardMode === 'edit'}
              saveStatus={saveStatus}
              submittedPhones={submittedSet}
              reviewDays={schedulePack.schedule?.needsHumanReview || []}
              gapList={gapList}
              onJumpDay={onJumpDay}
              onPatch={onPatch}
              fillViewport
              variant="admin"
            />
          ) : (
            <div style={{ ...S.card, color: '#6b7280' }}>尚無班表，請 JSON 匯入。</div>
          )}
        </div>

        <div style={S.card}>
          <h4 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 800 }}>工時統計</h4>
          {statsRows.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>—</div>}
          {statsRows.map(({ person, st }) => (
            <div key={person} style={{ fontSize: 12, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid #f3f4f6' }}>
              <strong>{person}</strong>
              {st ? (
                <div style={{ color: '#374151' }}>
                  {st.total_days ?? st.totalDays ?? 0} 天 · {st.total_hours ?? st.totalHours ?? 0} h
                </div>
              ) : (
                <div style={{ color: '#9ca3af' }}>未匯入統計</div>
              )}
            </div>
          ))}
          {gapList?.length > 0 && (
            <>
              <h4 style={{ margin: '12px 0 6px', fontSize: 13, fontWeight: 800 }}>待確認</h4>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#6b7280', maxHeight: 160, overflow: 'auto' }}>
                {gapList.slice(0, 20).map((g, i) => (
                  <li key={i}>{g.day ? `${g.day}日：` : ''}{g.reason}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
